import { cloneDeep, cloneDeepWith } from 'lodash';
import type { DependencyList } from 'react';
import { useRef } from 'react';
import { arraysAreDifferent } from '../functions/arrays-are-different';

/**
 * Clones the dependencies array, shallow-copying any object properties whose
 * keys appear in {@link ignoreProperties} instead of deep-cloning them.
 * When there are no ignored properties the faster {@link cloneDeep} is used.
 */
function cloneDependencies(dependencies: DependencyList, ignoreProperties: Array<string>): DependencyList {
  if (ignoreProperties.length === 0) {
    return cloneDeep(dependencies);
  }

  return cloneDeepWith(dependencies, (_value: unknown, key: string | number | undefined) => {
    if (typeof key === 'string' && ignoreProperties.includes(key)) {
      return _value;
    }
    // returning undefined tells lodash to use default deep-clone behaviour
  });
}

/**
 * A custom version of `useMemo` that contains better dependency comparison especially with arrays of objects.
 *
 * This is a stackoverflow article answer I used on how to create a custom `useMemo` hook
 * https://stackoverflow.com/a/73029270/3310602.
 * @param {() => T} factory - The function called when one of the dependencies have changed.
 * @param {DependencyList} dependencies - The list of dependencies use to trigger the factory function.
 * @param {Array<string>} ignoreProperties - If passed this will tell the equals comparison to ignore a particular key(s).
 * @returns T The value returned from the factory function.
 */
const useOdinMemo = <T>(factory: () => T, dependencies: DependencyList, ignoreProperties: Array<string> = []): T => {
  // Dependencies are cloned so the stored reference is not linked to the parameter value.
  // Properties in ignoreProperties are shallow-copied to avoid expensive deep-cloning.
  const _memo = useRef<[] | [DependencyList] | [DependencyList, T]>([]);
  const isChanged =
    _memo.current.length > 0 &&
    _memo.current[0] &&
    arraysAreDifferent(_memo.current[0], dependencies, ignoreProperties);

  if (_memo.current.length === 0 || isChanged) {
    _memo.current = [cloneDependencies(dependencies, ignoreProperties), factory()];
  }

  // @ts-expect-error
  return _memo.current[1];
};

export default useOdinMemo;
